import useStore from 'reusable/mobx-store-utils'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import AuthStore, { AuthStoreProvider } from './AuthStore'
import { Route, Routes } from 'react-router-dom'
import AuthForgotPassword from './forgot-password/AuthForgotPassword'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import AuthSignInFlow from './sign-in/AuthSignInFlow'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import { usePrevious } from 'reusable/utils/utils'
import AuthCustomTokenStore from './AuthCustomTokenStore'
import AuthValidateLinkBelongsToUser from './validate/AuthValidateLinkBelongsToUser'
import AuthValidateCustomToken from './validate/AuthValidateCustomToken'
import AuthXeroMarketplaceSignUp from './sign-up/AuthXeroMarketplaceSignUp'
import { oauthPaths } from '@gap/utils/auth/oauth-paths'
import SignUpAdvisor from './sign-up/advisor/SignUpAdvisor'
import { AdvisorSignUpEnum } from './sign-up/advisor/SignUpAdvisorStore'
import WebSocketProvider from 'websockets/WebSocketProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import MainRouter from 'features/main/main-router/MainRouter'
import AuthNonAdvisorSignUp from './sign-up/non-advisor/AuthNonAdvisorSignUp'
import AuthResetPassword from './reset-password/AuthResetPassword'
import useQuery from 'reusable/hooks/useQuery'
import AuthShare from './share/AuthShare'
import AuthShareFile from './share/AuthShareFile'
import { isLogoutReason, loggedOutReasonParam } from './logout-reason'
import { lazyWithRetry } from 'reusable/new-version/lazyWithRetry'

const MeetingRecordingPage = lazyWithRetry(() => import('features/meetings/meeting/in-person-recording/MeetingRecordingPage'))

export default observer(function AuthGuard() {
   const regionRouterStore = useRegionRouterStore()
   const authCustomTokenStore = useStore(() => new AuthCustomTokenStore())
   const previousPath = usePrevious(regionRouterStore.pathWithoutRegion)
   const queryString = useQuery()
   const authStore = useStore(sp => new AuthStore(sp), { regionRouterStore, authCustomTokenStore, previousPath })

   useEffect(() => {
      const rawLogoutReason = queryString.get(loggedOutReasonParam)
      // Cached bundles from before the loggedOutReason param can still emit the old one
      const hasLegacySessionTimedOutParam = !!queryString.get('displaySessionTimedOut')

      if (rawLogoutReason || hasLegacySessionTimedOutParam) {
         authStore.logoutReason = rawLogoutReason && isLogoutReason(rawLogoutReason) ? rawLogoutReason : 'sessionExpired'

         const url = new URL(window.location.href)
         url.searchParams.delete(loggedOutReasonParam)
         url.searchParams.delete('displaySessionTimedOut')

         window.history.replaceState({}, '', url.toString())
      }
   }, [queryString, authStore])

   // Reconfigure Amplify auth if needed
   useEffect(() => {
      const amplifyWasNotConfigured = !authStore.isAmplifyConfigured
      if (
         amplifyWasNotConfigured ||
         [previousPath, regionRouterStore.pathWithoutRegion].find(path =>
            oauthPaths.includes(path || '') || path?.startsWith('/xero-callback')
         )
      ) {
         authStore.configureAmplify()
      }
      if (amplifyWasNotConfigured) {
         authStore.initialise()
      }
   }, [authStore, regionRouterStore.pathWithoutRegion, previousPath])

   useEffect(() => {
      if (authStore.shouldBeInTrialSetupFlow) {
         authStore.ensureUserIsOnCorrectTrialStep()
      }
   }, [authStore, authStore.shouldBeInTrialSetupFlow, regionRouterStore])

   if (!authStore.initialised) {
      return null
   }

   return <AuthStoreProvider store={authStore}>
      <WebSocketProvider>
         <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Routes>
               {/* Unauthenticated users */}
               {!authStore.isAuthenticated && (
                  <>
                     <Route path="forgot-password" element={<AuthForgotPassword />} />
                     <Route path="signup/xero" element={<AuthXeroMarketplaceSignUp />} />
                     <Route path="*" element={<AuthSignInFlow />} />
                  </>
               )}

               {/* Authenticated users */}
               {authStore.isAuthenticated && (
                  <>
                     {authStore.shouldBeInTrialSetupFlow ? (
                        <Route path="trial/*" element={<SignUpAdvisor signUpAction={AdvisorSignUpEnum.trial} />} />
                     ) : (
                        <>
                           <Route path="reset-password/*" element={<AuthResetPassword />} />
                           <Route path="client-invitation/*" element={<AuthNonAdvisorSignUp />} />
                           <Route path="join/*" element={<AuthNonAdvisorSignUp />} />
                           <Route path="*" element={
                              <SkeletonLoader loading={authStore.loader.isLoading} width="100%">
                                 <MainRouter />
                              </SkeletonLoader>
                           } />
                        </>
                     )}
                  </>
               )}

               {/* Route for doing the password reset flow */}
               <Route path="auth/update/:token/:userId/*" element={<AuthValidateCustomToken attemptSignIn={true} />} />

               {/* Always accessible (authenticated or unauthenticated) */}
               <Route path="auth/validate/:token/:userId/*" element={<AuthValidateCustomToken attemptSignIn={false} />} />
               <Route path="auth/validate/client/:token/:userId/*" element={<AuthValidateCustomToken attemptSignIn={true} />} />
               <Route path="auth/validate/:userId/*" element={<AuthValidateLinkBelongsToUser />} />
               <Route path="trial/*" element={<SignUpAdvisor signUpAction={AdvisorSignUpEnum.trial} />} />
               <Route path="signup/xero" element={<SignUpAdvisor signUpAction={AdvisorSignUpEnum.completeXeroSignup} />} />
               <Route path="register/*" element={<SignUpAdvisor signUpAction={AdvisorSignUpEnum.register} />} />
               <Route path="share/:token" element={<AuthShare />} />
               <Route path="share-file/:token" element={<AuthShareFile />} />
               <Route path="meetings/:meetingId/scribe" element={
                  <React.Suspense fallback={null}>
                     <MeetingRecordingPage />
                  </React.Suspense>
               } />
            </Routes>
         </LocalizationProvider>
      </WebSocketProvider>
   </AuthStoreProvider>
})
